import { useCallback, useEffect, useRef, useState } from 'react'
import { getBuild, listBuilds } from '../buildApi.js'
import StatusAdvance from './StatusAdvance.jsx'
import { latestBridgeImage, parseProvenance } from '../gptBridge/bridgeModel.js'
import GptBridgePanel from '../gptBridge/GptBridgePanel.jsx'
import { imageStateOf, metaOf, rowsNeedingThumb, thumbOf, whenOf } from './handoff.js'
import {
  groupBuilds, unrecordedCreators, stampOf, hasArt, creatorOf,
  sortAndFilter, SORTS, RANGES, DEFAULT_SORT,
} from './handoffGroups.js'
import styles from './Studio.module.css'

/** Records opened just to learn who made the build. Same bound as thumbnails. */
const CREATOR_HYDRATE_CAP = 20

/**
 * BUILD HISTORY (Master Spec Section 10).
 *
 * `GET /api/artwork/builds` returns a bounded index (`{ ok, builds }`) so the
 * list never scans the store; opening a row reads the full record from
 * `GET /api/artwork/build/:id`.
 *
 * Status is HUMAN-GATED end to end — see StatusAdvance. Nothing on this panel
 * publishes, submits or advances anything by itself.
 */
export default function StudioBuilds({ statuses, refreshKey, onNextStep }) {
  const [rows, setRows] = useState([])
  const [state, setState] = useState('loading')
  const [err, setErr] = useState('')
  const [openId, setOpenId] = useState(null)
  const [record, setRecord] = useState(null)
  const [recordState, setRecordState] = useState('idle')
  /* buildId → thumbnail url, for rows whose index entry carried no preview. */
  const [thumbs, setThumbs] = useState({})
  /* buildId → { createdBy }, read from the record because the index omits it. */
  const [creators, setCreators] = useState({})
  /* Show every build by default. The toggle below is the visible control; it
   * never starts hidden, because a rep whose build is filtered out on arrival
   * concludes it was lost. */
  const [onlyArt, setOnlyArt] = useState(false)
  /* Brian's agreed default: most recent activity, any time. */
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [range, setRange] = useState('all')
  /* Which view the finish-in-GPT panel is pointed at, inside the open record. */
  const [bridgeView, setBridgeView] = useState('')
  const alive = useRef(true)

  useEffect(() => () => { alive.current = false }, [])

  const load = useCallback(async () => {
    setState('loading')
    try {
      /* ⚠️ THE CACHE-BUSTER IS HONEST ABOUT WHAT IT IS. Brian: *"it looks
       * stale"*. One cause is PROVEN and it is not a cache: the Bezel Drop
       * level writes no build record at all, so a sheet he made minutes ago
       * cannot appear here however hard the list refetches. That needs the
       * create route, and it is backend work.
       *
       * A second cause is UNPROVEN: whether anything between this call and the
       * store serves a cached page. `_t` makes each read a distinct URL so no
       * intermediate can answer it from a previous one. It is cheap and it
       * cannot hurt; it is not evidence that a cache existed. */
      const res = await listBuilds({ limit: 50, _t: Date.now() })
      setRows(res?.builds || [])
      setState('ready')
    } catch (e) {
      setErr(e?.message || 'Could not load build history.')
      setState('error')
    }
  }, [])

  useEffect(() => { load() }, [load, refreshKey])

  /**
   * THUMBNAIL HYDRATION — bounded, sequential, and never fatal.
   *
   * The index is a summary and may not carry a preview. Rather than couple the
   * row to a DTO the backend has not promised (hard rule #4), a row that CLAIMS
   * images but shows none reads its own record to find one.
   *
   * Bounded to `THUMB_HYDRATE_CAP` rows and run one at a time: the index exists
   * precisely so the list does not scan the store, and firing 50 parallel reads
   * to decorate a list would rebuild the scan it replaced. A failure costs that
   * row its picture and nothing else — the row still opens.
   */
  useEffect(() => {
    const want = rowsNeedingThumb(rows).filter((id) => !(id in thumbs))
    if (!want.length) return
    let stop = false
    ;(async () => {
      for (const id of want) {
        if (stop || !alive.current) return
        try {
          const res = await getBuild(id)
          const url = thumbOf(res?.record)
          if (!stop && alive.current) setThumbs((t) => ({ ...t, [id]: url }))
        } catch {
          /* No picture for this row. Not an error worth a banner. */
          if (!stop && alive.current) setThumbs((t) => ({ ...t, [id]: '' }))
        }
      }
    })()
    return () => { stop = true }
  }, [rows, thumbs])

  /**
   * CREATOR HYDRATION, bounded exactly like the thumbnails above.
   *
   * ⚠️ AND IT CORRECTS SOMETHING I GOT WRONG. I reported that the backend had
   * never captured who made a build. It has: the FULL record carries
   * `createdBy: {id, name}` on builds back to 25 August. Only
   * `GET /api/artwork/builds` omits it. So this is a DTO gap, not a data gap,
   * and the client can close it today by reading the records it is already
   * entitled to read. When the index starts returning `createdBy`, `creatorOf`
   * finds it on the row first and this loop stops doing anything.
   */
  useEffect(() => {
    const want = (rows || []).map((r) => r.buildId).filter((id) => id && !(id in creators)).slice(0, CREATOR_HYDRATE_CAP)
    if (!want.length) return
    let stop = false
    ;(async () => {
      for (const id of want) {
        if (stop || !alive.current) return
        try {
          const res = await getBuild(id)
          const rec = res?.record || {}
          if (!stop && alive.current) setCreators((c) => ({ ...c, [id]: { createdBy: rec.createdBy || '' } }))
        } catch {
          if (!stop && alive.current) setCreators((c) => ({ ...c, [id]: {} }))
        }
      }
    })()
    return () => { stop = true }
  }, [rows, creators])

  const open = useCallback(async (buildId) => {
    if (openId === buildId) { setOpenId(null); setRecord(null); return }
    setOpenId(buildId)
    setRecord(null)
    setRecordState('loading')
    try {
      const res = await getBuild(buildId)
      setRecord(res?.record || null)
      setRecordState('ready')
    } catch (e) {
      setRecordState('error')
      setErr(e?.message || 'Could not open that build.')
    }
  }, [openId])

  /** Re-read one record in place, after the bridge attaches an image to it. */
  const refreshRecord = useCallback(async (buildId) => {
    try {
      const res = await getBuild(buildId)
      if (!alive.current) return
      setRecord(res?.record || null)
      /* The row's image count comes from the index, which has not been
       * refetched, so update it here rather than showing "no renders" under a
       * picture the operator just watched arrive. */
      setRows((rs) => rs.map((r) => (r.buildId === buildId
        ? { ...r, imageCount: Math.max(Number(r.imageCount) || 0, 1) }
        : r)))
    } catch { /* the image is still owned; the panel already said so */ }
  }, [])

  function onAdvanced(next) {
    setRecord(next)
    setRows((rs) => rs.map((r) => (r.buildId === next.buildId ? { ...r, status: next.status } : r)))
  }

  if (state === 'loading') return <p className={styles.note}>Loading build history…</p>
  if (state === 'error') {
    return (
      <div className={styles.errorCard}>
        <p>{err}</p>
        <button type="button" className="btn" onClick={load}>Retry</button>
      </div>
    )
  }
  if (!rows.length) {
    return <p className={styles.note}>No builds recorded yet. Every render writes its record here before the images are produced.</p>
  }

  /* Creator → team → newest first. See handoffGroups.js for why creator is not
   * rep, and why grouping is by team in the absence of any `pack` field. */
  const withArt = rows.filter(hasArt).length
  const shown = onlyArt ? rows.filter(hasArt) : rows
  /* ⚠️ SORT AND RANGE ARE APPLIED BEFORE GROUPING, and groupBuilds keeps the
   * order it is handed. The undated rows come back SEPARATELY rather than
   * pinned to one end, because a row with no date sorted to the bottom reads
   * as the oldest thing in the list, which is a claim the data does not make. */
  const view = sortAndFilter(shown, { sort, range })
  /* The undated rows are appended AND announced AND chipped on the row itself.
   * Appending alone is the failure mode: last place in a newest-first list
   * reads as "oldest", which is a fact the record does not carry. */
  const undatedIds = new Set(view.undated.map((r) => r.buildId))
  const groups = groupBuilds([...view.rows, ...view.undated], creators)
  /* The gap is counted on the HYDRATED rows, so it shrinks to zero as the
   * records come back rather than reporting a gap that no longer exists. */
  /* Same precedence rule as groupBuilds: the row's own creator wins, and the
   * hydrated fragment is only consulted where the row has none. Written the
   * other way round, this counted 6 of 6 rows as having no creator while all
   * six carried one. */
  const gap = unrecordedCreators(rows.map((r) => (creatorOf(r).known ? r : (creators[r.buildId] || r))))

  return (
    <div className={styles.builds}>
      <div className={styles.buildsHead}>
        <span>
          {rows.length} build{rows.length === 1 ? '' : 's'}, by who made them
          {' · '}{withArt} with artwork, {rows.length - withArt} still waiting on the engine
        </span>
        <span className={styles.buildsActions}>
          {/* ⚠️ A VISIBLE CONTROL, AND IT STARTS OFF. Eight of twelve builds
              carry no images, and hiding them by default would tell a rep their
              work vanished. The empties are drawn quieter instead, and this is
              the switch that removes them, with its own count on the label. */}
          <button
            type="button"
            className={styles.linkBtn}
            aria-pressed={onlyArt}
            onClick={() => setOnlyArt((v) => !v)}
          >
            {onlyArt ? `Showing ${withArt} with artwork · show all ${rows.length}` : `Show only the ${withArt} with artwork`}
          </button>
          <button type="button" className={styles.linkBtn} onClick={load}>Refresh</button>
        </span>
      </div>

      {/* ⚠️ THE SORT NAMES ITS FIELD, IN THE CONTROL AND IN THE SENTENCE UNDER
          IT. createdAt and updatedAt are days apart on real rows, so "sort by
          date" would be a question with two answers. The default is most recent
          activity because a rep opening Hand off wants what moved. */}
      <div className={styles.handoffControls}>
        <label className={styles.handoffControl}>
          <span className="field-label">Order</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
        <label className={styles.handoffControl}>
          <span className="field-label">Date range</span>
          <select value={range} onChange={(e) => setRange(e.target.value)}>
            {RANGES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </label>
        {(sort !== DEFAULT_SORT || range !== 'all') && (
          <button type="button" className={styles.linkBtn} onClick={() => { setSort(DEFAULT_SORT); setRange('all') }}>
            Reset to most recent activity, any time
          </button>
        )}
      </div>

      <p className={styles.note}>
        {/* The maths and the wording come from the same object, so they cannot drift apart. */}
        Showing {view.rows.length} of {shown.length}, {view.describe}.
        {view.excluded > 0 && (view.excluded === 1
          ? ` 1 build falls outside ${view.range.label.toLowerCase()} and is hidden.`
          : ` ${view.excluded} builds fall outside ${view.range.label.toLowerCase()} and are hidden.`)}
        {/* ⚠️ BLANKS ARE NAMED, NOT SORTED. */}
        {/* ⚠️ THE VERB AGREES TOO. Pluralising only the noun produced "1 build
            carry no updatedAt value at all. They are listed last", which was
            on screen in the walkthrough. A sentence this load-bearing cannot
            read as broken English. */}
        {view.undated.length > 0 && (view.undated.length === 1
          ? ` 1 build carries no ${view.sort.field} value at all. It is listed last and marked "no date", not treated as oldest, and the date range does not exclude it because there is no date to compare.`
          : ` ${view.undated.length} builds carry no ${view.sort.field} value at all. They are listed last and marked "no date", not treated as oldest, and the date range does not exclude them because there is no date to compare.`)}
      </p>

      {/* ⚠️ THE DATA GAP IS STATED, NOT PAPERED OVER. If the index carries no
          author, that is the backend never having captured one — a gap that
          needs a creator field at create time, not a cleverer read here. Saying
          "unknown user" would read exactly like a recorded fact. */}
      {gap > 0 && (
        <p className={styles.note}>
          {gap} of these {rows.length} builds still show no creator. The record carries one, but
          <code> GET /api/artwork/builds</code> does not return it, so each row is read individually to find it and the
          rows above the read limit stay blank until the index carries the field. A build&apos;s creator is the
          signed-in user who made it; a rep is a different fact that belongs to the account, and the two are not
          mixed here.
        </p>
      )}
      {/* ⚠️ A BEZEL DROP SHEET CANNOT APPEAR HERE YET, and the list says so
          rather than letting its absence read as "nothing was made". The only
          route that creates a build record server-side is POST
          /api/artwork/render; the Bezel Drop level goes out through the GPT
          bridge and writes no record, so its sheets live in HubSpot File
          Manager with the provenance in the file name and nowhere in this
          list. That needs the create route. */}
      <p className={styles.note}>
        Bezel Drop sheets are not in this list yet. They land owned in HubSpot File Manager with their provenance in
        the file name, but nothing creates a build record for them, so there is no row to show. Levels 1 and 2 are
        complete here.
      </p>

      {groups.map((g) => (
      <section key={g.creator.id || 'unrecorded'} className={styles.buildGroup}>
        <div className={styles.buildGroupHead}>
          <span className={g.creator.known ? styles.buildCreator : styles.buildCreatorGap}>{g.creator.label}</span>
          <span className={styles.buildGroupCount}>{g.count} build{g.count === 1 ? '' : 's'}</span>
        </div>

        {g.teams.map((t) => (
        <div key={t.team.id || 'nojob'} className={styles.buildTeam}>
          {/* THE JOB, WITH THE LEVELS IT ACTUALLY HAS. There is no `pack` field
              anywhere in the data model, so the grouping is by TEAM and this
              says so by showing which of the three levels the job carries. */}
          <div className={styles.buildTeamHead}>
            <span className={t.team.known ? styles.buildTeamName : styles.buildCreatorGap}>{t.team.label}</span>
            <span className={styles.buildTeamLevels}>
              {t.levels.map((id) => <span key={id} className={styles.buildLevelChip}>{id}</span>)}
            </span>
          </div>

          <ul className={styles.buildList}>
        {t.builds.map((r) => {
          const img = imageStateOf(r)
          const src = thumbOf(r) || thumbs[r.buildId] || ''
          const meta = metaOf(r)
          const when = whenOf(r)
          return (
          <li key={r.buildId} className={`${styles.buildRow} ${hasArt(r) ? '' : styles.buildRowEmpty}`}>
            {/* ⚠️ THE BLANK IS MARKED ON THE ROW, not just counted above it.
                Without this the row simply sits at the bottom of a newest-first
                list and reads as the oldest build in the account. */}
            {undatedIds.has(r.buildId) && (
              <span className={styles.buildNoDate}>
                No {view.sort.field} recorded, so this row is not placed in the order
              </span>
            )}
            {/* THE ROW IS A CARD, NOT A GRID OF COLLIDING CELLS.
              *
              * The old row placed four children into a two-column grid in which
              * the date declared `grid-row: 1 / span 2` and no column, so it was
              * auto-placed into an implicit track ON TOP OF the title, and the
              * status pill was placed mid-flow, splitting the meta sentence.
              *
              * Structure now states the reading order outright: picture, then
              * title on its own line, then the facts, then when. Nothing spans,
              * nothing is auto-placed, so nothing can land on anything else. */}
            <button
              type="button"
              className={styles.buildBtn}
              onClick={() => open(r.buildId)}
              aria-expanded={openId === r.buildId}
            >
              {/* THE RENDER IS THE POINT OF THE ROW. The Artwork Studio is a
                * visual tool; a history of it with no pictures is a spreadsheet
                * of things you cannot see. */}
              <span className={styles.buildThumb} aria-hidden>
                {src
                  ? <img src={src} alt="" loading="lazy" />
                  : <span className={styles.buildThumbEmpty}>{img.kind === 'failed' ? '—' : '···'}</span>}
              </span>

              <span className={styles.buildMain}>
                <span className={styles.buildTitle}>{r.title || 'Untitled build'}</span>

                {/* Whole facts as separate items, so the row wraps BETWEEN them
                  * instead of breaking one long string a word per line. */}
                {/* ⚠️ THE LEVEL, IN WORDS, ON EVERY ROW. It was already on the
                  * record as `mode` and rendered as the bare word "generate",
                  * which reads as noise rather than as "this is level 2, a
                  * Generated concept". Same fact, named. */}
                <span className={styles.buildMeta}>
                  <span className={styles.buildLevelChip}>{r.level.n} · {r.level.label}</span>
                  {meta.map((m, i) => <span key={i} className={styles.buildMetaItem}>{m}</span>)}
                </span>

                {/* The image state is a SENTENCE when there is nothing to show.
                  * "0 images" reads as a failed build; these builds are saved
                  * and waiting on the server. */}
                <span className={`${styles.buildImages} ${img.kind === 'images' ? styles.buildImagesOk : ''}`}>
                  {img.line}
                </span>

                <span className={styles.buildFoot}>
                  {/* The pill is its OWN element on its own line — never inside
                    * a text run, which is what split the word "generate". */}
                  <span className={`${styles.badge} ${r.status === 'production' ? styles.badgeOk : ''}`}>{r.status}</span>
                  {/* ⚠️ TIME TO THE SECOND AND THE ID TAIL. Three UIL Golf rows
                    * sat 28 seconds apart and a date could not tell them apart,
                    * so the list read as duplicated when it was not. */}
                  {stampOf(r).when && <span className={styles.buildDate}>{stampOf(r).when}</span>}
                  {stampOf(r).id && <span className={styles.buildShortId}>{stampOf(r).id}</span>}
                </span>
              </span>
            </button>

            {/* The rep pathway is designed separately. This is the slot it lands
              * in — left deliberately, so adding the next step is a render here
              * and not another rebuild of the row. */}
            {onNextStep && (
              <div className={styles.buildNext}>{onNextStep(r)}</div>
            )}

            {openId === r.buildId && (
              <div className={styles.buildDetail}>
                {recordState === 'loading' && <p className={styles.note}>Opening…</p>}
                {record && (
                  <>
                    <div className={styles.buildGrid}>
                      <Row label="Build" value={record.buildId} />
                      <Row label="Beast" value={record.beastId} />
                      <Row label="Setting & output" value={record.settingPreset} />
                      <Row label="Scale" value={record.input?.scale} />
                      <Row label="Complexity" value={record.input?.complexity} />
                      <Row label="Championship" value={record.championshipId || 'not attached'} />
                    </div>
                    {!!(record.images || []).length && (
                      <div className={styles.thumbRow}>
                        {record.images.map((i) => {
                          const g = !(i.hostedUrl || i.url) && latestBridgeImage(record.history, i.view)
                          return (
                            <figure key={i.view} className={styles.thumb}>
                              {i.hostedUrl || i.url
                                ? <img src={i.hostedUrl || i.url} alt={i.label} />
                                : g
                                  ? <img src={g.url} alt={`${i.label} (from the GPT)`} />
                                  : <span className={styles.tileNoThumb}>{i.error ? 'failed' : 'no image'}</span>}
                              <figcaption>{i.label}{g ? ' · from the GPT' : ''}</figcaption>
                            </figure>
                          )
                        })}
                      </div>
                    )}
                    {!!(record.notes || []).length && (
                      <ul className={styles.noteList}>
                        {record.notes.map((n, i) => <li key={i}>{n}</li>)}
                      </ul>
                    )}

                    {/* ⚠️ FINISH THE BUILD FROM HERE. This is the gap that left
                      * eight builds empty forever.
                      *
                      * The bridge panel already existed per view, but ONLY on a
                      * fresh render result in the same session. Close the tab and
                      * the prompts were unreachable: the record still held five
                      * complete assembled prompts and there was no way to send
                      * any of them. So a rep's only visible move was to press
                      * Render again, which is why three UIL Golf builds sit 28
                      * seconds apart with zero images each.
                      *
                      * Nothing new is assembled here. It is the SAME record,
                      * the SAME prompt and the SAME owned-asset return leg. */}
                    {!!(record.prompts || []).length && (() => {
                      const views = record.prompts
                      /* NOT named `open`: that is the row-expand callback in
                       * this component's scope, and shadowing it here silently
                       * broke the refresh after an attach. */
                      const pending = views.filter((p) => {
                        const img = (record.images || []).find((i) => i.view === p.view)
                        return !(img && (img.hostedUrl || img.url)) && !latestBridgeImage(record.history, p.view)
                      })
                      if (!pending.length) return <p className={styles.note}>All requested views are attached. Inspect the proof set and record the quality review before customer presentation.</p>
                      const chosen = views.find((p) => p.view === bridgeView) || pending[0] || views[0]
                      const crest = record.input?.logoC?.url || ''
                      return (
                        <div className={styles.finishBox}>
                          <div className="field-label">
                            Finish this build in your GPT — {pending.length} of {views.length} view
                            {views.length === 1 ? '' : 's'} still has no image
                          </div>
                          <p className={styles.note}>
                            The engine assembled every prompt for this build and skipped only the billable image call.
                            Nothing here spends anything: it sends the prompt this record already holds.
                          </p>
                          <div className={styles.viewChips}>
                            {views.map((p) => {
                              const img = (record.images || []).find((i) => i.view === p.view)
                              const done = !!(img && (img.hostedUrl || img.url)) || !!latestBridgeImage(record.history, p.view)
                              return (
                                <button
                                  key={p.view}
                                  type="button"
                                  className={`${styles.viewChip} ${chosen.view === p.view ? styles.viewChipOn : ''}`}
                                  onClick={() => setBridgeView(p.view)}
                                >
                                  {p.label}{done ? ' ✓' : ''}
                                </button>
                              )
                            })}
                          </div>
                          <GptBridgePanel
                            buildId={record.buildId}
                            view={chosen.view}
                            viewLabel={chosen.label}
                            prompt={chosen.prompt}
                            crestUrl={crest}
                            eyebrow="Finish this view · no API spend"
                            /* Re-read the record so the new image and its
                             * [gpt-bridge] provenance line appear without a
                             * manual refresh. */
                            onAttached={() => refreshRecord(record.buildId)}
                          />
                        </div>
                      )
                    })()}

                    <StatusAdvance record={record} statuses={statuses} onAdvanced={onAdvanced} />
                    {!!(record.history || []).length && (
                      <ol className={styles.histList}>
                        {record.history.map((h, i) => {
                          /* A [gpt-bridge] entry is not a status move (the status
                           * is posted back unchanged); it is where an image came
                           * from. Say that instead of "draft → draft". */
                          const g = parseProvenance([h])[0]
                          return (
                            <li key={i}>
                              <span className={styles.histWhen}>{new Date(h.at).toLocaleString()}</span>
                              {g ? (
                                <>
                                  Image for {g.view || 'a view'} from the GPT ({g.route === 'extension' ? 'SR GPT Bridge' : 'uploaded by hand'}),
                                  mark drawn by the model. <a href={g.url} target="_blank" rel="noopener noreferrer">Owned copy</a>
                                </>
                              ) : (
                                <>
                                  {h.from ? `${h.from} → ${h.to}` : `created as ${h.to}`}
                                  {h.note ? ` — ${h.note}` : ''}
                                </>
                              )}
                            </li>
                          )
                        })}
                      </ol>
                    )}
                  </>
                )}
              </div>
            )}
          </li>
          )
        })}
          </ul>
        </div>
        ))}
      </section>
      ))}
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div className={styles.buildCell}>
      <span className="field-label">{label}</span>
      <span>{value == null || value === '' ? '—' : String(value)}</span>
    </div>
  )
}
